import config, { type Flavor } from '../config';

/**
 * Whether tags are how a client's entitlements get granted in this deployment.
 *
 * Tags are one of two mechanisms. Where a subscribed plan grants entitlements
 * instead, tags grant nothing — so exposing the field could only ever narrow a
 * user's access, never widen it, which is how a user who assigned themselves
 * their own client's tag ended up losing their portal.
 *
 * Each value has to match the model the core_api this build talks to actually
 * runs — `ACCESS_MODEL_BY_FLAVOR` in `srv/src/core_api/dashboard_access`, keyed
 * by the *backend* `FLAVOR`, which is a different env var on a different
 * process. A deployment is only its own flavor here when those two disagree
 * about it, which is why `logyca` and `logyca-bops` both exist.
 *
 * `Flavor` comes from `config.ts`, which is the single place a deployment is
 * named, and which explains why the missing-key error that keying by it
 * produces is not a gate today. `?? true` is the runtime counterpart, since
 * `REACT_APP_FLAVOR` is an untyped env var that can hold a value outside the
 * union. Falling back to enabled is the safe direction: it shows the field
 * rather than hiding a control a deployment might need.
 */
const TAGS_ENABLED_BY_FLAVOR: Record<Flavor, boolean> = {
  bops: true,
  gs1mex: false,
  logyca: false,
  // Logyca's UI, served by the bops core_api — whose `FLAVOR` is `bops`, and
  // whose access model is therefore the tag-based one. Tags are what grants
  // dashboards there (`client_dashboards` rows are matched against them, and
  // no tags matches nothing). Masters and policies are tag-gated server-side
  // too, but a Logyca-flavored build registers no policy route and only a
  // `WorkInProgress` placeholder for masters (see `app.tsx`), so dashboards
  // are the whole user-visible effect here.
  'logyca-bops': true,
};

export const tagsEnabled = TAGS_ENABLED_BY_FLAVOR[config.REACT_APP_FLAVOR as Flavor] ?? true;

/**
 * The tags to keep when a user is moved to a different client.
 *
 * Enabled clears them — they belonged to the previous client. Disabled returns
 * them untouched: the field is hidden, so clearing would discard stored values
 * the user never saw, and the edit request sends `newTags` either way.
 *
 * Call this instead of branching inline; that is what stops hiding a control
 * from quietly becoming a write.
 */
export const tagsAfterClientChange = (current: string[]): string[] => (tagsEnabled ? [] : current);
